import { supabase } from '../lib/supabase';

/**
 * Creates a Stripe Payment Intent via Supabase Edge Function.
 * Supports milestone-based payments: milestoneType = 'deposit' (30%) or 'completion' (70%).
 * The Stripe secret key never leaves the server.
 */
export const createPaymentIntent = async ({ paymentData, customerInfo }) => {
  const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
    body: { paymentData, customerInfo },
  });

  if (error) throw new Error(error.message ?? 'Failed to create payment intent');
  if (data?.error) throw new Error(data.error);

  return data; // { clientSecret, paymentIntentId, stripeCustomerId, recordId, milestoneType, milestoneAmount }
};

/**
 * Confirms a Stripe Payment Intent via Supabase Edge Function.
 * Updates the payment record status in the database.
 */
export const confirmPayment = async (paymentIntentId) => {
  const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
    body: { paymentIntentId },
  });

  if (error) throw new Error(error.message ?? 'Failed to confirm payment');
  if (data?.error) throw new Error(data.error);

  return data; // { success, status, record, amount, currency }
};

/**
 * Initiates the 70% completion payment for a project.
 * Called when the project/service is marked as complete.
 * Returns a clientSecret for the Stripe PaymentElement.
 */
export const createCompletionPaymentIntent = async ({ projectId, userId, vendorName, customerEmail, customerName }) => {
  const { data, error } = await supabase?.functions?.invoke('charge-completion-payment', {
    body: { projectId, userId, vendorName, customerEmail, customerName },
  });

  if (error) throw new Error(error.message ?? 'Failed to create completion payment');
  if (data?.error) throw new Error(data.error);

  return data; // { clientSecret, paymentIntentId, recordId, completionAmount, currency, alreadyExists }
};

/**
 * Fetches payment records for the current user.
 */
export const getUserPayments = async () => {
  const { data, error } = await supabase?.from('payments')?.select('*')?.order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data ?? [];
};

/**
 * Fetches a single payment by payment intent ID.
 */
export const getPaymentByIntentId = async (paymentIntentId) => {
  const { data, error } = await supabase?.from('payments')?.select('*')?.eq('payment_intent_id', paymentIntentId)?.single();

  if (error) throw new Error(error.message);
  return data;
};

/**
 * Fetches milestone payment summary for a project (deposit + completion status).
 */
export const getProjectMilestonePayments = async (projectId) => {
  const { data, error } = await supabase
    ?.from('payments')
    ?.select('id, amount, payment_status, metadata, created_at, milestone_type')
    ?.eq('project_id', projectId)
    ?.order('created_at', { ascending: true });

  if (error) throw new Error(error.message);

  const payments = data ?? [];
  const deposit = payments?.find(p => p?.metadata?.milestoneType === 'deposit' || p?.milestone_type === 'deposit');
  const completion = payments?.find(p => p?.metadata?.milestoneType === 'completion' || p?.milestone_type === 'completion');

  return {
    deposit: deposit ?? null,
    completion: completion ?? null,
    depositPaid: deposit?.payment_status === 'succeeded',
    completionPaid: completion?.payment_status === 'succeeded',
    totalPaid: payments?.filter(p => p?.payment_status === 'succeeded')?.reduce((sum, p) => sum + Number(p?.amount), 0) ?? 0,
  };
};
